import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  XMarkIcon,
  ChatBubbleLeftRightIcon,
  ClipboardDocumentCheckIcon,
  ChevronUpIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
import { deduplicateTranscriptTurns } from '../../pages/screenings/InterviewAnalysisTranscript';

/**
 * Standalone replay modal for a WhatsApp Agent interview — same information as the AI
 * Interview replay modal (per-question scores, answers, full transcript), but built as its
 * own small layout rather than reusing InterviewAnalysisTranscript wholesale: WhatsApp
 * interviews have no recording to play and no multi-dimension rubric/analytics breakdown,
 * so the video panel and "AI Score Breakdown"/"AI Insights" cards from that shared
 * component don't apply here and would only ever render empty placeholders.
 */
export default function WhatsAppInterviewReplayModal({ screeningId, candidate, onClose }) {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [innerTab, setInnerTab] = useState('questions');
  const [expandedQuestions, setExpandedQuestions] = useState(new Set());

  const candidateId = candidate?.id || candidate?._id;

  useEffect(() => {
    if (!screeningId || !candidateId) return;
    let cancelled = false;
    setLoading(true);
    setError('');
    axios
      .get(`/api/screenings/${screeningId}/whatsapp-agent-session/${candidateId}`)
      .then((res) => {
        if (!cancelled) setSession(res.data?.session || null);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err.response?.data?.message || 'Failed to load WhatsApp interview session');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [screeningId, candidateId]);

  const toggleExpanded = (key) => {
    setExpandedQuestions((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const overallScore = session?.scores?.overall;
  const scoreColor = overallScore >= 80 ? '#059669' : overallScore >= 60 ? '#d97706' : '#dc2626';
  const perQuestion = session?.rubric?.perQuestion || [];
  const candidateName = candidate?.name || session?.candidateName || 'Candidate';

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 p-4 sm:p-6">
      <div className="flex w-full max-w-3xl max-h-[90vh] flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div style={{ height: 3, background: 'linear-gradient(90deg, #25D366, #22C55E)' }} />
        <header className="flex items-center justify-between shrink-0 border-b border-gray-100 px-6 py-5">
          <div className="flex items-center gap-4">
            <div className="flex items-center justify-center w-9 h-9 rounded-xl bg-[#25D366]">
              <ChatBubbleLeftRightIcon className="h-4.5 w-4.5 text-white" style={{ width: 18, height: 18 }} />
            </div>
            <div>
              <h2 className="text-lg font-bold tracking-tight text-slate-900">
                {candidateName} — WhatsApp Interview
              </h2>
              <p className="text-xs mt-0.5 text-slate-500">Questions asked, answers, and per-question scores</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl p-2 text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </header>

        <div className="flex-1 min-h-0 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <span className="inline-block h-6 w-6 animate-spin rounded-full border-2 border-[#25D366]/60 border-t-transparent" />
            </div>
          ) : error ? (
            <div className="text-center py-20 text-sm text-red-600">{error}</div>
          ) : !session ? (
            <div className="text-center py-20 text-sm text-gray-500">No session data available</div>
          ) : (
            <div className="space-y-6">
              {overallScore != null && (
                <div className="flex items-center gap-4 rounded-2xl border border-gray-100 bg-gradient-to-br from-[#25D366]/5 to-white p-5">
                  <div className="relative shrink-0" style={{ width: 72, height: 72 }}>
                    <svg viewBox="0 0 36 36" className="w-full h-full -rotate-90">
                      <circle cx="18" cy="18" r="15.5" fill="none" stroke="#e5e7eb" strokeWidth="2.5" />
                      <circle
                        cx="18"
                        cy="18"
                        r="15.5"
                        fill="none"
                        stroke={scoreColor}
                        strokeWidth="2.5"
                        strokeDasharray={`${Math.min(overallScore, 100) * 0.975} 100`}
                        strokeLinecap="round"
                      />
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-xl font-extrabold" style={{ color: scoreColor }}>{overallScore}</span>
                    </div>
                  </div>
                  <div>
                    <p className="text-sm font-bold text-gray-900">WhatsApp Interview Score</p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {overallScore >= 80 ? 'Excellent performance' : overallScore >= 60 ? 'Good, room to improve' : 'Needs improvement'}
                    </p>
                  </div>
                </div>
              )}

              <div className="rounded-2xl border border-gray-200 overflow-hidden">
                <div className="flex border-b border-gray-100 bg-gray-50/50">
                  <button
                    type="button"
                    onClick={() => setInnerTab('questions')}
                    className={`flex-1 py-3 px-4 text-sm font-bold flex items-center justify-center gap-2 transition-colors ${innerTab === 'questions' ? 'text-[#1c9950] bg-white border-b-2 border-[#25D366]' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'}`}
                  >
                    <ClipboardDocumentCheckIcon className="w-4 h-4" /> Question Analysis
                  </button>
                  <button
                    type="button"
                    onClick={() => setInnerTab('transcript')}
                    className={`flex-1 py-3 px-4 text-sm font-bold flex items-center justify-center gap-2 transition-colors ${innerTab === 'transcript' ? 'text-[#1c9950] bg-white border-b-2 border-[#25D366]' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'}`}
                  >
                    <ChatBubbleLeftRightIcon className="w-4 h-4" /> Transcript
                  </button>
                </div>

                <div className="p-4">
                  {innerTab === 'questions' &&
                    (perQuestion.length > 0 ? (
                      <div className="space-y-2.5">
                        {perQuestion.map((q, idx) => {
                          const key = `question-${q.index ?? idx}`;
                          const isExpanded = expandedQuestions.has(key);
                          const score = q.score ?? 0;
                          const qScoreColor = score >= 80 ? '#059669' : score >= 60 ? '#d97706' : score > 0 ? '#ea580c' : '#dc2626';
                          const pct = Math.min(score, 100);
                          return (
                            <div key={key}>
                              <button
                                type="button"
                                onClick={() => toggleExpanded(key)}
                                className="w-full text-left rounded-xl p-3 transition-all duration-200 hover:bg-gray-50/80"
                                style={{ background: isExpanded ? 'rgb(249,250,251)' : 'transparent' }}
                              >
                                <div className="flex items-start gap-3">
                                  <div className="flex-shrink-0 relative w-12 h-12 flex items-center justify-center">
                                    <svg viewBox="0 0 36 36" className="w-12 h-12 -rotate-90">
                                      <circle cx="18" cy="18" r="15.5" fill="none" stroke="#f1f5f9" strokeWidth="3.2" />
                                      <circle
                                        cx="18"
                                        cy="18"
                                        r="15.5"
                                        fill="none"
                                        stroke={qScoreColor}
                                        strokeWidth="3.2"
                                        strokeDasharray={`${pct * 0.975} 100`}
                                        strokeLinecap="round"
                                      />
                                    </svg>
                                    <span className="absolute inset-0 flex items-center justify-center text-[13px] font-extrabold" style={{ color: qScoreColor }}>
                                      {q.score != null ? score : '—'}
                                    </span>
                                  </div>
                                  <div className="flex-1 min-w-0 pt-0.5">
                                    <span className="text-[14px] font-bold text-gray-900">Q{(q.index ?? idx) + 1}</span>
                                    <p className="text-[14px] text-gray-700 leading-snug line-clamp-2 font-medium mt-0.5">{q.prompt}</p>
                                  </div>
                                  <div className="flex-shrink-0 pt-2">
                                    {isExpanded ? <ChevronUpIcon className="h-4 w-4 text-gray-400" /> : <ChevronDownIcon className="h-4 w-4 text-gray-400" />}
                                  </div>
                                </div>
                              </button>
                              {isExpanded && (
                                <div className="ml-[52px] mr-2 mt-1 mb-2 space-y-3">
                                  {q.responses?.length > 0 && (
                                    <div>
                                      <div className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">Response</div>
                                      {q.responses.map((response, rIdx) => (
                                        <p key={rIdx} className="text-[13px] text-gray-700 leading-relaxed bg-white rounded-lg p-2.5 border border-gray-100">
                                          {response}
                                        </p>
                                      ))}
                                    </div>
                                  )}
                                  {q.notes && (
                                    <div>
                                      <div className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">AI Notes</div>
                                      <p className="text-[13px] text-gray-500 leading-relaxed italic">{q.notes}</p>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="text-center py-12 text-sm text-gray-500">No questions recorded</div>
                    ))}

                  {innerTab === 'transcript' &&
                    (() => {
                      const transcript = deduplicateTranscriptTurns(session.transcript || []);
                      if (transcript.length === 0) {
                        return <div className="text-center py-12 text-sm text-gray-500">No transcript available</div>;
                      }
                      return (
                        <div className="space-y-3">
                          {transcript.map((turn, idx) => (
                            <div key={idx} className="flex gap-2.5">
                              <div className="flex-shrink-0">
                                {turn.role === 'ai' ? (
                                  <div className="w-7 h-7 rounded-full bg-[#25D366]/10 flex items-center justify-center">
                                    <ChatBubbleLeftRightIcon className="w-4 h-4 text-[#1c9950]" />
                                  </div>
                                ) : (
                                  <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">
                                    <span className="text-white text-[10px] font-extrabold">{candidateName.charAt(0).toUpperCase()}</span>
                                  </div>
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <span className="text-[13px] font-bold text-gray-800">
                                  {turn.role === 'ai' ? 'TalentCrux AI' : candidateName}
                                </span>
                                <p className="text-[14px] text-gray-700 leading-relaxed mt-0.5 whitespace-pre-line">{turn.text}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      );
                    })()}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
